import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { IndianRupee, TrendingUp, AlertCircle, Users, Download } from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const YEARS = ['2024', '2025', '2026'];
const MONTHS = ['01','02','03','04','05','06','07','08','09','10','11','12'];
const MONTH_NAMES_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_NAMES_FULL = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const COLORS = ['#1B2A1B', '#D4AF37', '#4F734F', '#759975', '#A3BCA3', '#C5A028', '#3B5A3B', '#2F472F'];

export default function ReportsPage() {
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [month, setMonth] = useState('');
  const [revenue, setRevenue] = useState(null);
  const [outstanding, setOutstanding] = useState(null);
  const [clientWise, setClientWise] = useState([]);
  const [serviceWise, setServiceWise] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const mp = month ? `&month=${month}` : '';
        const [revRes, outRes, clRes, svcRes] = await Promise.all([
          api.get(`/reports/revenue?year=${year}${mp}`),
          api.get('/reports/outstanding'),
          api.get(`/reports/client-wise?year=${year}${mp}`),
          api.get(`/reports/service-wise?year=${year}${mp}`)
        ]);
        setRevenue(revRes.data);
        setOutstanding(outRes.data);
        setClientWise(clRes.data);
        setServiceWise(svcRes.data);
      } catch { toast.error('Failed to load reports'); }
      finally { setLoading(false); }
    };
    fetchAll();
  }, [year, month]);

  const handleExport = async (type) => {
    try {
      const mp = month ? `&month=${month}` : '';
      const response = await api.get(`/export/reports?report_type=${type}&year=${year}${mp}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a'); a.href = url; a.download = `${type}_report_${year}${month ? '_' + month : ''}.csv`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Report exported');
    } catch { toast.error('Export failed'); }
  };

  const chartData = (revenue?.monthly || []).map(m => {
    const monthIdx = parseInt(m._id?.split('-')[1]) - 1;
    return { name: MONTH_NAMES_SHORT[monthIdx] || m._id, revenue: m.total_revenue, paid: m.paid, unpaid: m.unpaid, count: m.invoice_count };
  });

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="reports-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-4xl font-semibold text-[#111B11] tracking-tight">Reports</h1>
          <p className="text-sm text-[#4F734F] mt-1">Revenue, outstanding, and business analytics</p>
        </div>
        <div className="flex gap-2">
          <Select value={month || 'all'} onValueChange={(v) => setMonth(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-36 input-field" data-testid="report-month-select"><SelectValue placeholder="All Months" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Months</SelectItem>{MONTHS.map((m, i) => <SelectItem key={m} value={m}>{MONTH_NAMES_FULL[i]}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="w-28 input-field" data-testid="report-year-select"><SelectValue /></SelectTrigger>
            <SelectContent>{YEARS.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="stat-card" data-testid="total-revenue-card">
          <div className="flex items-center gap-2 mb-1"><IndianRupee size={16} className="text-[#D4AF37]" /><p className="text-xs uppercase font-semibold text-[#4F734F]">Total Revenue</p></div>
          <p className="font-heading text-3xl font-light text-[#111B11]">{(revenue?.total_revenue || 0).toLocaleString('en-IN')}</p>
        </div>
        <div className="stat-card">
          <div className="flex items-center gap-2 mb-1"><TrendingUp size={16} className="text-green-600" /><p className="text-xs uppercase font-semibold text-[#4F734F]">Collected</p></div>
          <p className="font-heading text-3xl font-light text-green-600">{(revenue?.total_paid || 0).toLocaleString('en-IN')}</p>
        </div>
        <div className="stat-card">
          <div className="flex items-center gap-2 mb-1"><AlertCircle size={16} className="text-red-500" /><p className="text-xs uppercase font-semibold text-[#4F734F]">Outstanding</p></div>
          <p className="font-heading text-3xl font-light text-red-600">{(outstanding?.total_outstanding || 0).toLocaleString('en-IN')}</p>
          <p className="text-xs text-olive-500 mt-1">{outstanding?.count || 0} unpaid invoices</p>
        </div>
        <div className="stat-card">
          <div className="flex items-center gap-2 mb-1"><Users size={16} className="text-olive-600" /><p className="text-xs uppercase font-semibold text-[#4F734F]">Clients Billed</p></div>
          <p className="font-heading text-3xl font-light text-[#111B11]">{clientWise.length}</p>
        </div>
      </div>

      <Tabs defaultValue="revenue" className="space-y-6">
        <TabsList>
          <TabsTrigger value="revenue" data-testid="tab-revenue">Revenue</TabsTrigger>
          <TabsTrigger value="outstanding" data-testid="tab-outstanding">Outstanding</TabsTrigger>
          <TabsTrigger value="clients" data-testid="tab-clients">Client-wise</TabsTrigger>
          <TabsTrigger value="services" data-testid="tab-services">Service-wise</TabsTrigger>
        </TabsList>

        {/* Revenue Tab */}
        <TabsContent value="revenue">
          <div className="bg-white border border-[#E6EDE6] rounded-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-lg font-medium text-[#213021]">Monthly Revenue - {year}{month ? ` / ${MONTH_NAMES_FULL[parseInt(month)-1]}` : ''}</h2>
              <Button onClick={() => handleExport('revenue')} variant="outline" size="sm" className="gap-1" data-testid="export-revenue"><Download size={14} /> Export CSV</Button>
            </div>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E6EDE6" />
                  <XAxis dataKey="name" tick={{ fill: '#4F734F', fontSize: 12 }} />
                  <YAxis tick={{ fill: '#4F734F', fontSize: 12 }} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
                  <Tooltip formatter={(v) => [`INR ${v.toLocaleString('en-IN')}`, '']} contentStyle={{ borderRadius: '4px', border: '1px solid #E6EDE6' }} />
                  <Bar dataKey="paid" name="Paid" fill="#1B2A1B" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="unpaid" name="Unpaid" fill="#D4AF37" radius={[2, 2, 0, 0]} />
                  <Legend />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-center text-olive-400 py-12">No revenue data for {year}</p>}
          </div>
        </TabsContent>

        {/* Outstanding Tab */}
        <TabsContent value="outstanding">
          <div className="bg-white border border-[#E6EDE6] rounded-sm overflow-hidden">
            <div className="p-4 bg-red-50 border-b border-red-100">
              <p className="text-sm font-semibold text-red-700">Total Outstanding: INR {(outstanding?.total_outstanding || 0).toLocaleString('en-IN')} ({outstanding?.count || 0} invoices)</p>
            </div>
            <table className="w-full text-sm">
              <thead><tr className="bg-[#F4F7F4]"><th className="py-2 px-4 text-left text-xs font-medium text-[#4F734F]">Invoice #</th><th className="py-2 px-4 text-left text-xs font-medium text-[#4F734F]">Date</th><th className="py-2 px-4 text-left text-xs font-medium text-[#4F734F]">Client</th><th className="py-2 px-4 text-right text-xs font-medium text-[#4F734F]">Amount</th><th className="py-2 px-4 text-xs font-medium text-[#4F734F]">Status</th></tr></thead>
              <tbody>
                {(outstanding?.invoices || []).length === 0 ? <tr><td colSpan={5} className="text-center py-8 text-olive-400">No outstanding invoices</td></tr> :
                (outstanding?.invoices || []).map(inv => (
                  <tr key={inv._id} className="border-b border-[#E6EDE6]">
                    <td className="py-2 px-4 font-mono text-xs text-[#D4AF37]">{inv.invoice_number}</td>
                    <td className="py-2 px-4">{inv.invoice_date}</td>
                    <td className="py-2 px-4 font-medium">{inv.client_name}</td>
                    <td className="py-2 px-4 text-right font-mono font-medium text-red-600">{inv.total?.toLocaleString('en-IN')}</td>
                    <td className="py-2 px-4"><span className="rounded-full px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700">{inv.payment_status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Client-wise Tab */}
        <TabsContent value="clients">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-[#E6EDE6] rounded-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-lg font-medium text-[#213021]">Client Revenue</h3>
                <Button onClick={() => handleExport('client-wise')} variant="outline" size="sm" className="gap-1" data-testid="export-client"><Download size={14} /> CSV</Button>
              </div>
              {clientWise.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={clientWise.slice(0, 8)} dataKey="total_billed" nameKey="_id" cx="50%" cy="50%" outerRadius={100} label={({ _id, percent }) => `${(_id || '').substring(0, 10)} (${(percent * 100).toFixed(0)}%)`} labelLine={false}>
                      {clientWise.slice(0, 8).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => [`INR ${v.toLocaleString('en-IN')}`, 'Billed']} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <p className="text-center text-olive-400 py-12">No data</p>}
            </div>
            <div className="bg-white border border-[#E6EDE6] rounded-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead><tr className="bg-[#F4F7F4]"><th className="py-2 px-4 text-left text-xs font-medium text-[#4F734F]">Client</th><th className="py-2 px-4 text-center text-xs font-medium text-[#4F734F]">Invoices</th><th className="py-2 px-4 text-right text-xs font-medium text-[#4F734F]">Billed</th><th className="py-2 px-4 text-right text-xs font-medium text-[#4F734F]">Paid</th></tr></thead>
                <tbody>
                  {clientWise.map((c, i) => (
                    <tr key={i} className="border-b border-[#E6EDE6]">
                      <td className="py-2 px-4 font-medium text-[#111B11]">{c._id}</td>
                      <td className="py-2 px-4 text-center">{c.invoice_count}</td>
                      <td className="py-2 px-4 text-right font-mono">{c.total_billed?.toLocaleString('en-IN')}</td>
                      <td className="py-2 px-4 text-right font-mono text-green-600">{c.total_paid?.toLocaleString('en-IN')}</td>
                    </tr>
                  ))}
                  {clientWise.length === 0 && <tr><td colSpan={4} className="text-center py-8 text-olive-400">No data</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Service-wise Tab */}
        <TabsContent value="services">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-[#E6EDE6] rounded-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-lg font-medium text-[#213021]">Revenue by Service</h3>
                <Button onClick={() => handleExport('service-wise')} variant="outline" size="sm" className="gap-1" data-testid="export-service"><Download size={14} /> CSV</Button>
              </div>
              {serviceWise.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={serviceWise.slice(0, 8)} dataKey="total_revenue" nameKey="_id" cx="50%" cy="50%" outerRadius={100} label={({ _id, percent }) => `${(_id || '').substring(0, 12)} (${(percent * 100).toFixed(0)}%)`} labelLine={false}>
                      {serviceWise.slice(0, 8).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => [`INR ${v.toLocaleString('en-IN')}`, 'Revenue']} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : <p className="text-center text-olive-400 py-12">No data</p>}
            </div>
            <div className="bg-white border border-[#E6EDE6] rounded-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead><tr className="bg-[#F4F7F4]"><th className="py-2 px-4 text-left text-xs font-medium text-[#4F734F]">Service</th><th className="py-2 px-4 text-center text-xs font-medium text-[#4F734F]">Count</th><th className="py-2 px-4 text-right text-xs font-medium text-[#4F734F]">Revenue</th></tr></thead>
                <tbody>
                  {serviceWise.map((s, i) => (
                    <tr key={i} className="border-b border-[#E6EDE6]">
                      <td className="py-2 px-4 font-medium text-[#111B11]">{s._id}</td>
                      <td className="py-2 px-4 text-center">{s.count}</td>
                      <td className="py-2 px-4 text-right font-mono">{s.total_revenue?.toLocaleString('en-IN')}</td>
                    </tr>
                  ))}
                  {serviceWise.length === 0 && <tr><td colSpan={3} className="text-center py-8 text-olive-400">No data</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
